import {
  type AutoCompleteResponseFragment,
  useDebounce,
  useGetAutoCompleteAddressQuery,
  useValidateAddressInLocalizationMutation,
} from "@stepapp/core";
import {
  Block,
  BottomSheet,
  Button,
  Input,
  Loader,
  Typography,
  palette,
  spacing,
} from "@stepapp/ui";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import { useAppSelector } from "../../hooks";
import type { ReservationFormParams } from "../../store/draftReservation/types";
import { AutocompleteItem } from "../AutocompleteItem";
import LocationDisclaimerSheet from "./LocationDisclaimerSheet";

type Props = {
  isVisible: boolean;
  setModalVisible: Dispatch<SetStateAction<boolean>>;
  setValue: UseFormSetValue<ReservationFormParams>;
  streetAddress1: string;
};

const AddressAutocompleteModal = ({
  isVisible,
  setModalVisible,
  setValue,
  streetAddress1,
}: Props) => {
  const { t } = useTranslation();
  const { localizationId, city } = useAppSelector(({ appConfig }) => appConfig);
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(streetAddress1);

  const debouncedAddress = useDebounce(selectedAddress, 500);

  const [validateAddress] = useValidateAddressInLocalizationMutation();
  const { data, loading } = useGetAutoCompleteAddressQuery({
    variables: { payload: { address: `${city}, ${debouncedAddress}` } },
    skip: debouncedAddress.length < 3 || !isVisible,
  });

  const addresses = useMemo(
    () => data?.generateAutocompleteForAddress || [],
    [data?.generateAutocompleteForAddress],
  );

  const handleAddressPress = useCallback(
    async (autocompleteAddress: AutoCompleteResponseFragment) => {
      const { data } = await validateAddress({
        variables: {
          payload: {
            city: autocompleteAddress?.city || "",
            localizationId,
            postalOrZipCode: autocompleteAddress?.postalCode || "",
            streetAddress1: autocompleteAddress?.address || "",
            streetAddress2: "",
          },
        },
      });
      if (!data?.validateAddressInLocalization) {
        setValue("address.streetAddress1", "");
        return setNotAvailableLocation(true);
      }

      setValue("address.postalOrZipCode", autocompleteAddress?.postalCode || "");
      setValue("address.streetAddress1", autocompleteAddress?.address || "");
      setValue("address.city", autocompleteAddress?.city || city || "");
      setSelectedAddress(autocompleteAddress?.address || "");
      setModalVisible(false);
    },
    [city, localizationId, setValue, setModalVisible, validateAddress],
  );

  const handleModalClose = useCallback(() => {
    setModalVisible(false);
    setSelectedAddress(streetAddress1);
  }, [streetAddress1, setModalVisible]);

  return (
    <BottomSheet
      contentStyle={styles.container}
      toggle={() => handleModalClose()}
      visible={isVisible}
    >
      <Typography variant="largeTitle">
        {t("pages.serviceDateAndAddress.provideAddressTitle")}
      </Typography>

      <Block paddingBottom={2} paddingTop={1}>
        <Typography type="secondary" variant="paragraph">
          {t("pages.serviceDateAndAddress.provideAddressToCheckAvailability")}
        </Typography>
      </Block>

      <Input
        testID="autocompleteModalInput"
        onFocus={() => {}}
        onChangeText={(v) => setSelectedAddress(v)}
        placeholder={t("components.input.placeholder.typeAddress")}
        value={selectedAddress}
      />

      <Block minHeight={40}>
        {loading ? (
          <Block alignCenter paddingTop={2}>
            <Loader />
          </Block>
        ) : (
          <>
            {addresses.length > 0 ? (
              <ScrollView style={styles.addressDropdown} testID="autocompleteDropdown">
                {addresses.map((address, index) => (
                  <AutocompleteItem
                    {...address}
                    divider={index !== addresses.length - 1}
                    key={address.latitude}
                    onPress={() => handleAddressPress(address)}
                  />
                ))}
              </ScrollView>
            ) : (
              <></>
            )}
          </>
        )}
      </Block>

      <Block paddingVertical={3}>
        <Button label={t("common.continue")} onPress={() => {}} type="primary" />
      </Block>

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />
    </BottomSheet>
  );
};

export default AddressAutocompleteModal;

const styles = StyleSheet.create({
  addressDropdown: {
    backgroundColor: palette.common.white,
    borderRadius: spacing.radius,
    boxShadow: "2px 2px 10px 0px rgba(0, 0, 0, 0.2)",
    maxHeight: spacing.unit * 40,
    padding: spacing.unit,
  },
  container: {
    padding: spacing.unit * 4,
  },
});
